import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { industryCatalog, type GenesisSession, type Industry } from "@/entities/genesis";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";

interface Props {
  session: GenesisSession;
  onNext: (industryKey: string | null, customDescription: string | null) => Promise<void>;
}

export function DiscoveryStep({ session, onNext }: Props): JSX.Element {
  const cat = useQuery({ queryKey: ["genesis", "industries"], queryFn: industryCatalog, staleTime: Infinity });
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<string | null>(session.industry_key);
  const [custom, setCustom] = useState(session.custom_description ?? "");
  const [submitting, setSubmitting] = useState(false);

  const filtered: { name: string; industries: Industry[] }[] = useMemo(() => {
    if (!cat.data) return [];
    if (!q.trim()) return cat.data.categories;
    const needle = q.trim().toLowerCase();
    return cat.data.categories
      .map((c) => ({
        name: c.name,
        industries: c.industries.filter(
          (i) => i.label.toLowerCase().includes(needle) || i.key.includes(needle),
        ),
      }))
      .filter((c) => c.industries.length > 0);
  }, [cat.data, q]);

  const canContinue = Boolean(selected) || custom.trim().length >= 3;

  return (
    <div className="grid gap-10 lg:grid-cols-[1.05fr_1fr]" data-testid="discovery-step">
      <div className="space-y-8">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#c7a25a]">
            One question
          </p>
          <h2
            className="mt-3 font-serif text-4xl leading-[1.1] tracking-tight text-[#f5efe6] md:text-5xl"
            style={{ fontFamily: "'Fraunces', serif" }}
          >
            What kind of business{" "}
            <span className="italic text-[#c7a25a]">are you building</span>?
          </h2>
          <p className="mt-4 max-w-lg text-[15px] leading-relaxed text-[#f5efe6]/70">
            Pick from the library, or describe it in your own words.
            Both work equally well.
          </p>
        </div>

        <div className="space-y-3">
          <label htmlFor="genesis-custom" className="block font-mono text-[10px] uppercase tracking-[0.3em] text-[#f5efe6]/60">
            Describe Your Business
          </label>
          <textarea
            id="genesis-custom"
            data-testid="discovery-custom-input"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder="e.g. Indoor dog park with a coffee bar. Or: Drone-based roof inspection service."
            rows={3}
            className="w-full resize-none rounded-2xl border border-[#f5efe6]/12 bg-[#131a17] px-5 py-4 text-[15px] leading-relaxed text-[#f5efe6] placeholder:text-[#f5efe6]/30 focus:border-[#c7a25a]/60 focus:outline-none focus:ring-2 focus:ring-[#c7a25a]/20"
          />
        </div>

        <Button
          onClick={async () => {
            if (!canContinue) return;
            setSubmitting(true);
            try {
              await onNext(selected, custom.trim() || null);
            } finally {
              setSubmitting(false);
            }
          }}
          disabled={!canContinue || submitting}
          data-testid="discovery-continue"
          className="h-13 rounded-full bg-[#c7a25a] px-8 text-[15px] font-medium text-[#0d1310] hover:bg-[#d4b06e] disabled:opacity-40"
        >
          {submitting ? "Reading…" : "Continue →"}
        </Button>
      </div>

      <div className="rounded-3xl border border-[#f5efe6]/8 bg-[#0f1613]/80 p-6">
        <div className="mb-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#f5efe6]/60">
            Or pick from the library
          </p>
          <p className="mt-1 text-[13px] text-[#f5efe6]/50">
            {cat.data?.total ?? "…"} industries · fresh ones added weekly
          </p>
        </div>
        <Input
          data-testid="discovery-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search industries…"
          className="mb-4 border-[#f5efe6]/12 bg-[#131a17] text-[#f5efe6] placeholder:text-[#f5efe6]/30 focus:border-[#c7a25a]/60"
        />
        <div className="max-h-[420px] space-y-5 overflow-y-auto pr-2" data-testid="discovery-list">
          {filtered.map((cat_) => (
            <div key={cat_.name}>
              <p className="mb-2 font-mono text-[9px] uppercase tracking-[0.3em] text-[#c7a25a]/80">
                {cat_.name}
              </p>
              <div className="flex flex-wrap gap-2">
                {cat_.industries.map((ind) => (
                  <button
                    key={ind.key}
                    type="button"
                    onClick={() => setSelected(ind.key)}
                    data-testid={`discovery-chip-${ind.key}`}
                    className={`rounded-full border px-3.5 py-1.5 text-[13px] transition ${
                      selected === ind.key
                        ? "border-[#c7a25a] bg-[#c7a25a]/15 text-[#c7a25a]"
                        : "border-[#f5efe6]/10 bg-transparent text-[#f5efe6]/80 hover:border-[#c7a25a]/40 hover:text-[#f5efe6]"
                    }`}
                  >
                    {ind.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {!filtered.length && (
            <p className="text-[13px] text-[#f5efe6]/50">
              Nothing matched "{q}". Your custom description works fine on its own.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
